import { makeStyles } from "@mui/styles";
import { Box, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { AppToolbar, DatePickerWrapper, DatePickerComponent, MIN_DATE, MAX_DATE } from "../components/AppToolbar";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import { SelectComponent } from "../components/SelectComponent";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import { useMaxStoredDate } from "../hooks/useStoredDate";
import useWindowSize from "../hooks/useWindowSize";
import TimeOffReport from "../models/TimeOffReport";
import TimeoffService from "../services/TimeoffService";
import { RemainingVacancies } from "../utils/enums/RemainingVacancies";
import { Months } from "../utils/OpsDateUtils";
import useTableHeight from "../hooks/useTableHeight";
import { useLocation } from "react-router-dom";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { Routes } from "../types/Routes";
import { filtersBarStyle } from "../theme";

export default function TimeOffReportList() {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const { isMobileWidth } = useWindowSize();
  const classes = useStyles();

  const [selectedDate, setSelectedDate] = useMaxStoredDate();
  const [month, setMonth] = useState<string>(Months.ALL);
  const [remainingVacancies, setRemainingVacancies] = useState<string>(RemainingVacancies.ALL);
  const [userNameFilter, setUserNameFilter] = useState<string>("");
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();
  const searchParams = new URLSearchParams(useLocation().search);
  const nameFilter = searchParams.get("name");
  const monthFilter = searchParams.get("month");
  const yearFilter = searchParams.get("year");
  const { updateUrl } = useUpdateUrl(Routes.TIME_OFF_REPORT, searchParams);

  useEffect(() => {
    const date = new Date();

    if (monthFilter !== null) {
      setMonth(monthFilter);
    } else {
      searchParams.set("month", month);
    }

    if (yearFilter !== null) {
      date.setFullYear(parseInt(yearFilter));
      setSelectedDate(date);
    } else {
      searchParams.set("year", date.getFullYear().toString());
    }

    if (nameFilter !== null && nameFilter !== "") {
      setUserNameFilter(nameFilter);
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderUserNameField = (
    <TextField
      label="User name"
      value={userNameFilter}
      onChange={(e) => {
        setUserNameFilter(e.target.value);
        searchParams.set("name", e.target.value);
        updateUrl();
      }}
      autoComplete="off"
      size="small"
    />
  );

  const renderFilters = () => {
    const mobileFilters = (
      <div className={classes.mobileFilters}>
        {renderUserNameField}
        <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    );

    const desktopFilters = (
      <div className={classes.desktopFilters}>
        {renderUserNameField}
        <SelectComponent
          label={"Month"}
          value={month}
          setValue={(value) => {
            setMonth(value);
            searchParams.set("month", value);
            updateUrl();
          }}
          options={Object.values(Months)}
          customStyle={{ margin: "0 0.5em 0 1.5em" }}
          size="small"
        />
        <DatePickerWrapper>
          <DatePickerComponent
            views="year"
            label="Year"
            value={selectedDate}
            inputFormat="yyyy"
            minDate={MIN_DATE}
            maxDate={MAX_DATE}
            onChange={(selectedDate) => {
              setSelectedDate(selectedDate!);
              searchParams.set("year", selectedDate!.getFullYear().toString());
              updateUrl();
            }}
          />
        </DatePickerWrapper>
      </div>
    );
    return isMobileWidth() ? mobileFilters : desktopFilters;
  };

  const renderDialogFilters = (
    <>
      <SelectComponent
        label={"Month"}
        value={month}
        setValue={setMonth}
        options={Object.values(Months)}
        customStyle={{ margin: "0 0.5em 0 1.5em" }}
        size="small"
      />
      <div className={classes.datePicker}>
        <DatePickerWrapper>
          <DatePickerComponent
            views="year"
            label="Year"
            value={selectedDate}
            inputFormat="yyyy"
            minDate={MIN_DATE}
            maxDate={MAX_DATE}
            onChange={(selectedDate) => setSelectedDate(selectedDate!)}
          />
        </DatePickerWrapper>
      </div>
    </>
  );

  const getContent = (page: number, pageSize: number): Promise<PagedResult<TimeOffReport>> => {
    return TimeoffService.getTimeOffReport(
      selectedDate.getFullYear(),
      Object.keys(Months).indexOf(month.toUpperCase()),
      page,
      pageSize,
      userNameFilter,
      remainingVacancies,
    ).then((response) => response.getContentOrThrowError());
  };

  const getHeader = () => {
    return <AppToolbar title={t("Time off report")}>{renderFilters()}</AppToolbar>;
  };

  const getBody = () => {
    return (
      <Box style={{ borderRadius: 8 }}>
        <TableBasedCrud<TimeOffReport>
          maxHeight={calculateMaxHeight(false)}
          stickyHeader
          stickyPagination
          elementName={t("Time off report")}
          denseTable
          delayFetch={userNameFilter}
          onDataFetch={(pageNumber: number, count: number) => getContent(pageNumber, count)}
          tableColumns={[
            {
              header: t("Team member"),
              content: (instance) => <>{instance.teamMember}</>,
            },
            {
              header: t("Vacation"),
              content: (instance) => <>{instance.vacation ? instance.vacation : "-"}</>,
            },
            {
              header: t("Illness"),
              content: (instance) => <>{instance.illness ? instance.illness : "-"}</>,
            },
            {
              header: t("Study"),
              content: (instance) => <>{instance.study ? instance.study : "-"}</>,
            },
            {
              header: t("Other"),
              content: (instance) => <>{instance.other ? instance.other : "-"}</>,
            },
            {
              header: t("Total"),
              content: (instance) => <>{instance.total}</>,
            },
          ]}
          renderDialogContent={() => <></>}
          hideActions
        />

        {/* Dialog to manage filters  */}
        <FilterDialog title={"Time off"} setIsOpen={setIsOpen} isOpen={isOpen}>
          {renderDialogFilters}
        </FilterDialog>
      </Box>
    );
  };

  return <AppContainerFullHeight bottomSectionRef={bottomSectionRef} topSection={getHeader()} bottomSection={getBody()} />;
}

const useStyles = makeStyles(
  (theme) => ({
    desktopFilters: {
      ...filtersBarStyle(),
      "&>div ": {
        margin: "0 !important",
      },
    },
    mobileFilters: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      rowGap: "10px",
      "&> div:first-child": {
        minWidth: "150px",
        width: "100%",
        marginLeft: "0",
      },
    },
    dialogFilters: {
      padding: "30px 15px",
      rowGap: "10px",
      display: "flex",
      flexDirection: "column",
      "& div": {
        margin: "0 !important",
        width: "100% !important",
      },
    },
    datePicker: {
      "&>div>div": {
        width: "100% !important",
      },
    },
  }),
  { index: 1 },
);
